import { LoadingButton, Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from "@mui/lab";
import { AppBar, Autocomplete, Button, Card, CardActions, CardContent, CardHeader, Chip, Dialog, FormControl, InputLabel, MenuItem, OutlinedInput, Select, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Toolbar } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { Printer } from "@untitled-ui/icons-react";
import { BarChartSquarePlus, Check, ChevronRight, Pencil01, PiggyBank01, Route, Trash01, X } from "@untitled-ui/icons-react/build/esm";
import XIcon from "@untitled-ui/icons-react/build/esm/X";
import { useFormik } from "formik";
import { pick } from "lodash";
import { useRouter } from "next/router";
import { FC, useEffect, useMemo, useState } from "react";
import { useBoolean } from "usehooks-ts";
import * as Yup from "yup";
import { CustomFieldRenderer, CustomFieldsDialog } from "~/components/custom-fields-dialog";
import { ProductsTableSummary, TableItem, TableItemDisplay, TableSubtotalAddons } from "~/components/products-table-summary";
import { useAppRouter } from "~/hooks/use-app-router";
import { useTillSummary } from "~/hooks/use-till-summary";
import { $dayjs, DATESTRING_FORMAT, DATETIMESTRING_FORMAT } from "~/libs/dayjs";
import { useGetAgents } from "~/queries/useGetAgents";
import { useGetClients } from "~/queries/useGetClients";
import { useGetCounters } from "~/queries/useGetCounters";
import { GetCurrentCompanyResults, useGetCurrentCompany, useGetCurrentCompanyCache } from "~/queries/useGetCurrentCompany";
import { useGetFileCustomFields } from "~/queries/useGetCustomFields";
import { GetFileByIdResults, useGetCurrentFile } from "~/queries/useGetFileById";
import { useGetProcessesCache } from "~/queries/useGetProcesses";
import { useGetProductOptions } from "~/queries/useGetProductOptions";
import { useGetProducts } from "~/queries/useGetProducts";
import { useGetUsers } from "~/queries/useGetUsers";
import { usePayForFile } from "~/queries/usePayForFile";
import { useUpdateFile } from "~/queries/useUpdateFile";
import { useResetTill, useSetTillIsOpen, useTill, useUpdateTillFileInputSnapshot, useUpdateTillUpdateFileInput } from "~/store";
import { CUSTOM_FIELD_MODEL, CUSTOM_FIELD_TYPE, CustomFieldValueInput, PayForFileMutationVariables, PAYMENT_VERIFICATION_UPLOAD_STATUS, PaymentVerification, ROLE } from "~/types/__generated/gql/graphql";
import { TillFileInput } from "~/types/store";
import { DefineProcessPathsDialog } from "./define-process-paths-dialog";
import { FileStatus } from "./file-status";
import { InvoicePdfDialog } from "./invoice-pdf-dialog";
import { OrderSheetDialog } from "./order-sheet-dialog";
import { useMeCache } from "~/queries/useMe";
import { COUNTER_TYPE, IMAGE_FILE_EXTENSIONS, PAYMENT_VERIFICATION_STATUS } from "~served/configs";
import { DateTimePicker } from "@mui/x-date-pickers";
import { bigMath, calculateFile, formatNumber } from "~served/utils";
import { useCancelFileById } from "~/queries/useCancelFileById";
import { DeleteFileDialog } from "./delete-file-dialog";
import { usePermissionFilters } from "~/hooks/use-permission-filters";
import { useUpdateReceiptPayment } from "~/queries/useUpdateReceiptPayment";
import CommentSection from "~/components/comment-section";
import { FileInputLike, FileProductInputLike, FileProductOptionInputLike } from "~served/utils/calculations/types";
import { Download } from "@mui/icons-material";
import { PaymentVerificationButton } from "./payment-verification-button";
import PdfIcon01 from "~/icons/untitled-ui/duocolor/pdf-icon-01";
import { useUpdatePaymentVerification } from "~/queries/useUpdatePaymentVerification";
const PAYMENT_VERIFICATION_UPLOAD_STATUS_MAP = {
  [PAYMENT_VERIFICATION_UPLOAD_STATUS.DEPOSIT_PAYMENT]: "Deposit",
  [PAYMENT_VERIFICATION_UPLOAD_STATUS.PARTIAL_PAYMENT]: "Partial",
  [PAYMENT_VERIFICATION_UPLOAD_STATUS.FINAL_PAYMENT]: "Final"
};
export const ViewFileModal = () => {
  const {
    currentActions: {
      isViewingFile
    },
    router
  } = useAppRouter();
  const {
    data: company
  } = useGetCurrentCompanyCache();
  const {
    data: me
  } = useMeCache();
  const {
    data: processes = []
  } = useGetProcessesCache();
  const {
    data: file,
    refetch: refetchFile,
    isLoading: isFileLoading
  } = useGetCurrentFile();
  const till = useTill();
  const setTillIsOpen = useSetTillIsOpen();
  const resetTill = useResetTill();
  const updateUpdateFileInput = useUpdateTillUpdateFileInput();
  const updateFileInputSnapshot = useUpdateTillFileInputSnapshot();
  const {
    data: agents = [],
    isLoading: isAgentsLoading
  } = useGetAgents();
  const {
    data: clients = [],
    isLoading: isClientsLoading
  } = useGetClients();
  const {
    data: users = [],
    isLoading: isUsersLoading
  } = useGetUsers();
  const {
    data: counters = [],
    isLoading: isCountersLoading
  } = useGetCounters();
  const {
    updateFileInputSummary,
    tillItemsSummary
  } = useTillSummary();
  const updateFile = useUpdateFile();
  const cancelFileById = useCancelFileById();
  const {
    value: isProcessPathsDialogOpen,
    setTrue: openProcessPathsDialog,
    setFalse: closeProcessPathsDialog
  } = useBoolean(false);
  const {
    value: isFileMoving,
    toggle: toggleFileMoving
  } = useBoolean(false);
  const {
    value: isFileProcessing,
    toggle: toggleFileProcessing
  } = useBoolean(false);
  const {
    value: isCustomFieldsDialogOpen,
    setTrue: openCustomFieldsDialog,
    setFalse: closeCustomFieldsDialog
  } = useBoolean(false);
  const {
    data: customFields = []
  } = useGetFileCustomFields();
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, CustomFieldValueInput>>({});
  const [paymentVerification, setPaymentVerification] = useState<PaymentVerification["attachment"] | undefined>();
  const nextProcessId = useMemo(() => {
    if (!file?.processPaths.length) return;
    const currentProcessIndex = file.processPaths.findIndex(path => path._process._id === file._process._id);
    return file.processPaths[currentProcessIndex + 1]?._process?._id;
  }, [file]);
  const processOptions = useMemo(() => processes.map(column => ({
    label: column.name,
    value: column._id
  })), [processes]);
  const onClose = () => {
    const newQuery = router.query;
    delete newQuery.fileId;
    resetTill();
    return router.push({
      query: newQuery
    });
  };
  const handleCancelFileById = async (id: string): Promise<void> => {
    try {
      await cancelFileById.mutateAsync({
        id: id
      });
      await onClose();
    } catch (_) {
      await onClose();
    }
  };
  const handleMoveFileToColumn = async (columnId: string, closeView: boolean): Promise<void> => {
    try {
      toggleFileMoving();
      await updateFile.mutateAsync({
        id: file!._id,
        input: {
          process: columnId
        }
      });
      toggleFileMoving();
      if (closeView) await onClose();
    } catch (_) {
      toggleFileMoving();
    }
  };
  const handleFileProcessedStatusChanged = async (): Promise<void> => {
    try {
      toggleFileProcessing();
      await updateFile.mutateAsync({
        id: file!._id,
        input: {
          isProcessed: !file!.isProcessed
        }
      });
      toggleFileProcessing();
    } catch (_) {
      toggleFileProcessing();
    }
  };
  useEffect(() => {
    if (isViewingFile && file) {
      const input: TillFileInput = {
        // skip null check to allow for unassigning agent
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        agent: file._agent?._id ?? null,
        client: file._client?._id ?? null,
        isProcessed: file.isProcessed,
        process: file._process._id,
        products: file.products.map(product => ({
          ...pick(product, ["_id", "product", "quantity", "noVat", "originalPrice", "originalPriceAddons", "listedPrice", "type", "category", "subtotalAddons", "createdAt", "updatedAt"]),
          options: product.options.map(option => pick(option, ["_id", "option", "quantity", "originalPrice", "originalPriceAddons", "listedPrice", "subtotalAddons"]))
        })) as TillFileInput["products"],
        assignees: file._assignees.map(assignee => assignee._id),
        processPaths: file.processPaths.map(processPath => ({
          process: processPath._process._id,
          leadTime: processPath.leadTime
        })),
        counterConfigs: file.counterConfigs,
        cancelledProducts: file.cancelledProducts.map(product => ({
          ...pick(product, ["_id", "product", "quantity", "noVat", "originalPrice", "originalPriceAddons", "listedPrice", "type", "category", "subtotalAddons", "createdAt", "updatedAt"]),
          options: product.options.map(option => pick(option, ["_id", "option", "quantity", "originalPrice", "originalPriceAddons", "listedPrice", "subtotalAddons"]))
        })) as TillFileInput["cancelledProducts"],
        customFields: file.customFields,
        fileSuffixComment: file.fileSuffixComment,
        receiptSuffixComment: file.receiptSuffixComment,
        issueDate: file.issueDate,
        dueDate: file.dueDate,
        isCancelled: file.isCancelled
      };
      updateUpdateFileInput(input);
      updateFileInputSnapshot(input);
      if (file.customFields) {
        setCustomFieldValues(file.customFields.reduce((acc, field) => {
          return {
            ...acc,
            [field.customField]: {
              customField: field.customField,
              dateTimeValue: field.dateTimeValue,
              selectValues: field.selectValues,
              textValue: field.textValue
            }
          };
        }, {}));
      }
      if (router.query.openOrderSheet) openOrderSheetDialog();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewingFile, file]);
  const [currentPaymentVerification, setCurrentPaymentVerification] = useState<PaymentVerification | undefined>();
  const {
    value: isPaymentDialogOpen,
    setTrue: openPaymentDialog,
    setFalse: closePaymentDialog
  } = useBoolean(false);
  const {
    value: isInvoiceDialogOpen,
    setTrue: openInvoiceDialog,
    setFalse: closeInvoiceDialog
  } = useBoolean(false);
  const {
    value: isOrderSheetDialogOpen,
    setTrue: openOrderSheetDialog,
    setFalse: closeOrderSheetDialog
  } = useBoolean(false);
  const {
    value: isDeleteFileDialogOpen,
    setTrue: openDeleteFileDialogOpen,
    setFalse: closeDeleteFileDialogOpen
  } = useBoolean(false);
  const {
    value: isViewPaymentVerificationDialogDialogOpen,
    setTrue: openViewPaymentVerificationDialogDialog,
    setFalse: closeViewPaymentVerificationDialogDialog
  } = useBoolean(false);
  const {
    value: isPaymentDialogWithPaymentVerification,
    setTrue: openPaymentDialogWithPaymentVerification,
    setFalse: closePaymentDialogWithPaymentVerification
  } = useBoolean(false);
  const handleClosePaymentDialogWithPaymentVerification = async () => {
    await refetchFile();
    setCurrentPaymentVerification(undefined);
    closePaymentDialogWithPaymentVerification();
  };
  const {
    data: products = []
  } = useGetProducts();
  const {
    data: productOptions = []
  } = useGetProductOptions();
  const productsTableItems = useMemo<TableItem[]>(() => updateFileInputSummary.products.map(product => ({
    id: product._id.toString(),
    name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
    quantity: product.quantity,
    subtotal: product.subtotal,
    options: product.options.map(option => ({
      id: option._id.toString(),
      name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
      quantity: option.quantity,
      subtotal: option.subtotal
    })),
    subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
  })), [updateFileInputSummary.products, productOptions, products]);
  const fileItemsDisplay = useMemo<TableItemDisplay>(() => ({
    prev: tillItemsSummary.prev.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons,
      createdAt: product.createdAt,
      updatedAt: product.updatedAt
    })),
    add: tillItemsSummary.add.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    })),
    remove: tillItemsSummary.remove.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    }))
  }), [productOptions, products, tillItemsSummary.add.products, tillItemsSummary.prev.products, tillItemsSummary.remove.products]);
  const isAdjustmentNeeded = useMemo(() => {
    if (!file?._receipt) return false;
    const allPayments = {
      payments: file._receipt.payments.flatMap(p => p.payments.flatMap(p1 => ({
        payment_type: p1.payment_type.toString(),
        amount: p1.amount
      })))
    };
    const {
      grossAmount
    } = calculateFile({
      ...(file as unknown as FileInputLike),
      products: file.products as unknown as FileProductInputLike[],
      cancelledProducts: file.cancelledProducts as unknown as FileProductInputLike[]
    });
    const paymentAmount = allPayments.payments.reduce((acc, cur) => acc + cur.amount, 0);
    if (paymentAmount > grossAmount) return true;
    if (file._receipt.isFullyPaid && paymentAmount < grossAmount) return true;
    return false;
  }, [file]);
  const handleClosePaymentDialog = async () => {
    await refetchFile();
    void closePaymentDialog();
  };
  const handleOpenViewPaymentVerificationDialog = (attachment: PaymentVerification["attachment"]) => {
    if (!attachment) return;
    setPaymentVerification(attachment);
    openViewPaymentVerificationDialogDialog();
  };
  const handleCloseViewPaymentVerificationDialog = () => {
    setPaymentVerification(undefined);
    closeViewPaymentVerificationDialogDialog();
  };
  const isLoading = isFileLoading || isAgentsLoading || isClientsLoading || isUsersLoading || isCountersLoading;
  return <Dialog open fullWidth maxWidth="xl" scroll="paper" data-sentry-element="Dialog" data-sentry-component="ViewFileModal" data-sentry-source-file="view-file-modal.tsx">
			{isDeleteFileDialogOpen && <DeleteFileDialog handleConfirm={(fileId: string) => handleCancelFileById(fileId)} onClose={closeDeleteFileDialogOpen} />}
			{isCustomFieldsDialogOpen && <CustomFieldsDialog model={CUSTOM_FIELD_MODEL.FILE} onClose={closeCustomFieldsDialog} />}
			{isPaymentDialogOpen && <PaymentDialog onClose={handleClosePaymentDialog} />}
			{currentPaymentVerification && isPaymentDialogWithPaymentVerification && <PaymentDialogWithPaymentVerification paymentVerification={currentPaymentVerification} onClose={handleClosePaymentDialogWithPaymentVerification} />}

			{isViewPaymentVerificationDialogDialogOpen && <ViewPaymentVerificationDialog attachment={paymentVerification!} onClose={handleCloseViewPaymentVerificationDialog} />}
			{isInvoiceDialogOpen && <InvoicePdfDialog fixDiscount={updateFileInputSummary.fixDiscountAmount} onClose={closeInvoiceDialog} />}
			{isOrderSheetDialogOpen && <OrderSheetDialog onClose={async () => {
      const newRouter = router.query;
      delete newRouter.openOrderSheet;
      await router.push({
        query: {
          ...newRouter
        }
      });
      closeOrderSheetDialog();
    }} />}
			<Stack sx={{
      height: "100dvh",
      overflow: "auto"
    }} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
				{isLoading ? <>
						<Stack direction="row" justifyContent="space-between" spacing={1}>
							<Skeleton variant="rounded" height={40} sx={{
            flex: 1
          }} />
							<Skeleton variant="circular" height={40} width={40} />
						</Stack>
						<Box>
							<Skeleton variant="rounded" height={40} />
						</Box>
						<Box>
							<Grid container spacing={3}>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="circular" height={40} width={40} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12}>
									<Skeleton variant="rounded" height={240} sx={{
                flex: 1
              }} />
								</Grid>
							</Grid>
						</Box>
					</> : <>
						{isProcessPathsDialogOpen && <DefineProcessPathsDialog initialValues={till.updateFileInput?.processPaths ?? []} onDone={async values => {
          await updateFile.mutateAsync({
            id: file!._id,
            input: {
              processPaths: values
            }
          });
          updateUpdateFileInput({
            processPaths: values as TillFileInput["processPaths"]
          });
          closeProcessPathsDialog();
        }} onCancel={closeProcessPathsDialog} />}
						<Box sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          backgroundColor: "background.paper",
          p: 2
        }} display="flex" flexDirection="column" gap={2}>
							<Box>
								<Grid container spacing={2}>
									<Grid xs={12} md={4}>
										<div>
											<Grid container spacing={1}>
												<Grid xs={4}>
													{file?.counterConfigs?.file && <TextField select size="small" label={`${company?.fileAlias.singular ?? "File"} Prefix`} sx={{
                        width: "100%"
                      }} value={file?.counterConfigs?.file?._counter?._id ?? ""} onChange={async e => {
                        await updateFile.mutateAsync({
                          id: file._id,
                          input: {
                            counterConfigs: {
                              file: {
                                counter: e.target.value
                              }
                            }
                          }
                        });
                        updateUpdateFileInput({
                          counterConfigs: {
                            file: {
                              counter: e.target.value
                            }
                          }
                        });
                      }}>
															{counters.filter(c => c.type === COUNTER_TYPE.FILE).map(counter => <MenuItem key={counter._id} value={counter._id} disabled={counter.isDisabled}>
																		{counter.isPredefinedFormat ? "YYYYMM" : counter.prefix}
																	</MenuItem>)}
														</TextField>}
												</Grid>
												<Grid xs={4}>
													{file?.counterConfigs?.file && <OutlinedInput disabled fullWidth value={file?.counterConfigs?.file?.index ? String(file?.counterConfigs?.file?.index || 0).padStart(file?.counterConfigs?.file?._counter?.suffix_digit || 0, "0") : ""} placeholder={`${company?.fileAlias.singular ?? "File"} Prefix index goes here`} />}
												</Grid>
												<Grid xs={4}>
													<TextField label={`${company?.fileAlias.singular ?? "File"}# Comment`} fullWidth defaultValue={till.updateFileInput?.fileSuffixComment ?? ""} onBlur={async e => {
                        const comment = e.target.value ?? "";
                        await updateFile.mutateAsync({
                          id: file!._id,
                          input: {
                            fileSuffixComment: comment
                          }
                        });
                        updateUpdateFileInput({
                          fileSuffixComment: comment
                        });
                      }} />
												</Grid>
											</Grid>
										</div>
									</Grid>
									<Grid xs={12} md={4}>
										<div>
											<Grid container spacing={1}>
												<Grid xs={4}>
													{file?.counterConfigs?.invoice && <TextField select label="Invoice Prefix" size="small" sx={{
                        width: "100%"
                      }} value={file?.counterConfigs?.invoice?._counter?._id ?? ""} onChange={async e => {
                        await updateFile.mutateAsync({
                          id: file._id,
                          input: {
                            counterConfigs: {
                              invoice: {
                                counter: e.target.value
                              }
                            }
                          }
                        });
                        updateUpdateFileInput({
                          counterConfigs: {
                            invoice: {
                              counter: e.target.value
                            }
                          }
                        });
                      }}>
															{counters.filter(c => c.type === COUNTER_TYPE.INVOICE).map(counter => <MenuItem key={counter._id} value={counter._id}>
																		{counter.isPredefinedFormat ? "YYYYMM" : counter.prefix}
																	</MenuItem>)}
														</TextField>}
												</Grid>
												<Grid xs={4}>
													{file?.counterConfigs?.invoice && <OutlinedInput disabled fullWidth value={file?.counterConfigs?.invoice?.index ? String(file?.counterConfigs?.invoice?.index || 0).padStart(file?.counterConfigs?.invoice?._counter?.suffix_digit || 0, "0") : ""} placeholder="Invoice prefix index goes here" />}
												</Grid>
												<Grid xs={4}>
													<TextField label="Invoice# Comment" fullWidth defaultValue={till.updateFileInput?.receiptSuffixComment ?? ""} onBlur={async e => {
                        const comment = e.target.value ?? "";
                        await updateFile.mutateAsync({
                          id: file!._id,
                          input: {
                            receiptSuffixComment: comment
                          }
                        });
                        updateUpdateFileInput({
                          receiptSuffixComment: comment
                        });
                      }} />
												</Grid>
											</Grid>
										</div>
									</Grid>
									<Grid xs={12} md={4}>
										<div>
											<Grid container spacing={2}>
												<Grid xs={5.5}>
													<DateTimePicker sx={{
                        width: "100%"
                      }} label="Issue Date" value={till.updateFileInput?.issueDate ? new Date(till.updateFileInput?.issueDate) : null} onChange={async value => {
                        if (!value) return;
                        await updateFile.mutateAsync({
                          id: file!._id,
                          input: {
                            issueDate: value.toISOString()
                          }
                        });
                        updateUpdateFileInput({
                          issueDate: value.toISOString()
                        });
                      }} />
												</Grid>
												<Grid xs={5.5}>
													<DateTimePicker sx={{
                        width: "100%"
                      }} label="Due Date" value={till.updateFileInput?.dueDate ? new Date(till.updateFileInput?.dueDate) : null} onChange={async value => {
                        if (!value) return;
                        await updateFile.mutateAsync({
                          id: file!._id,
                          input: {
                            dueDate: value.toISOString()
                          }
                        });
                        updateUpdateFileInput({
                          dueDate: value.toISOString()
                        });
                      }} />
												</Grid>
												<Grid xs={1}>
													<Stack justifyContent="flex-end" alignItems="center" direction="row" spacing={1}>
														<IconButton onClick={onClose}>
															<SvgIcon>
																<XIcon />
															</SvgIcon>
														</IconButton>
													</Stack>
												</Grid>
											</Grid>
										</div>
									</Grid>
								</Grid>
							</Box>
							<Box>
								<div>
									{!file?.processPaths?.length && <FileStatus prefix="Move to" onChange={handleMoveFileToColumn} isLoading={isFileMoving} options={processOptions} value={file?._process?._id ?? processOptions[0]?.value} />}
									{!!file?.processPaths?.length && <>
											<LoadingButton variant="contained" endIcon={<SvgIcon>
														<ChevronRight />
													</SvgIcon>} disabled={!nextProcessId} loading={isFileMoving} onClick={() =>
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                handleMoveFileToColumn(nextProcessId!, true)}>
												Move to next process
											</LoadingButton>
										</>}
									<LoadingButton color="success" variant={file?.isProcessed ? "contained" : "outlined"} startIcon={file?.isProcessed ? <SvgIcon>
													<Check />
												</SvgIcon> : null} loading={isFileProcessing} sx={{
                ml: 2
              }} onClick={handleFileProcessedStatusChanged}>
										{file?.isProcessed ? "Processed" : "Mark processed"}
									</LoadingButton>
									<Button startIcon={<SvgIcon>
												<Printer />
											</SvgIcon>} onClick={() => openOrderSheetDialog()}>
										Order Sheet
									</Button>
									<Button startIcon={<SvgIcon>
												<Printer />
											</SvgIcon>} onClick={() => openInvoiceDialog()}>
										{file?._receipt?.isFullyPaid ? "Receipt" : "Invoice"}
									</Button>
									<Button onClick={() => {
                setTillIsOpen(true);
              }} startIcon={<SvgIcon>
												<Pencil01 />
											</SvgIcon>}>
										Add Products
									</Button>
								</div>
							</Box>
							<Divider />
						</Box>

						<Grid container spacing={3} sx={{
          p: 3
        }}>
							<Grid container spacing={3} xs={12} md={6} height="fit-content">
								<Grid xs={12} sm={4}>
									<Typography color="text.secondary" variant="caption">
										Last updated
									</Typography>
								</Grid>
								<Grid xs={12} sm={8}>
									<Typography variant="caption">
										{updateFile.isLoading ? "Saving..." : $dayjs(file?.updatedAt).format(DATETIMESTRING_FORMAT)}
									</Typography>
								</Grid>
								<Grid xs={12} sm={4}>
									<Typography color="text.secondary" variant="caption">
										Created by
									</Typography>
								</Grid>
								<Grid xs={12} sm={8}>
									<Stack direction="row" alignItems="center" spacing={1}>
										<Avatar src={file?._user?.avatar ?? undefined} />
										<Stack>
											<Typography component="div" variant="caption">
												{file?._user?.firstName ?? "N/A"}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												<i>
													{$dayjs(file?.createdAt).format(DATETIMESTRING_FORMAT)}
												</i>
											</Typography>
										</Stack>
									</Stack>
								</Grid>
								<Grid xs={12} sm={4}>
									<Typography color="text.secondary" variant="caption">
										Process paths
									</Typography>
								</Grid>
								<Grid xs={12} sm={8}>
									{!!file?.processPaths?.length && <Timeline sx={{
                m: 0,
                py: 0
              }}>
											{file.processPaths.slice(0, 3).map((path, index, current) => {
                  return <TimelineItem key={index} sx={{
                    "::before": {
                      content: "unset"
                    }
                  }}>
															<TimelineSeparator>
																<TimelineDot color={path._process._id === file?._process._id ? "primary" : "grey"} />
																{index < 2 && !!current[index + 1] && <TimelineConnector />}
																{index === 2 && !!file.processPaths[3] && <TimelineConnector />}
															</TimelineSeparator>
															<TimelineContent>
																{path._process.name}
															</TimelineContent>
														</TimelineItem>;
                })}
										</Timeline>}
									<Button startIcon={<SvgIcon>
												<Route />
											</SvgIcon>} onClick={openProcessPathsDialog}>
										Define process paths
									</Button>
								</Grid>
								<Grid xs={12} sm={4}>
									<Typography color="text.secondary" variant="caption">
										Assignees
									</Typography>
								</Grid>
								<Grid xs={12} sm={8}>
									<Autocomplete multiple fullWidth disableCloseOnSelect options={users} value={users.filter(user => till.updateFileInput?.assignees?.includes(user._id)) ?? null} isOptionEqualToValue={(option, value) => option._id === value._id} getOptionLabel={option => option.firstName} onChange={async (_, values) => {
                await updateFile.mutateAsync({
                  id: file!._id,
                  input: {
                    assignees: values.map(user => user._id)
                  }
                });
                updateUpdateFileInput({
                  assignees: values.map(user => user._id)
                });
              }} renderInput={params => <TextField {...params} label="Users" />} renderTags={(value, getTagProps) => value.map((value, index) => <Chip {...getTagProps({
                index
              })} key={value._id} label={value.firstName} avatar={<Avatar src={value.avatar ?? undefined} />} />)} renderOption={(props, option) => <li {...props}>
												<Avatar src={option.avatar ?? undefined} />
												&nbsp;{option.firstName}
											</li>} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Typography color="text.secondary" variant="caption">
										Client
									</Typography>
								</Grid>
								<Grid xs={12} sm={8}>
									<Autocomplete fullWidth options={clients} renderOption={(props, option, {
                inputValue
              }) => {
                const label = `${option.companyIssuedId}-${option.name}`;
                const matches = label.toLowerCase().indexOf(inputValue.toLowerCase());
                const parts = matches !== -1 ? [label.substring(0, matches), label.substring(matches, matches + inputValue.length), label.substring(matches + inputValue.length)] : [label];
                return <li {...props} key={option._id} style={{
                  whiteSpace: "pre-wrap"
                }}>
													{parts.map((part, index) => <Box key={index} component="span" sx={part.toLowerCase() === inputValue.toLowerCase() ? {
                    fontWeight: 800,
                    bgcolor: "primary.main"
                  } : {}}>
															{part}
														</Box>)}
												</li>;
              }} value={clients.find(client => client._id === till.updateFileInput?.client) ?? null} isOptionEqualToValue={(option, value) => option._id === value?._id} getOptionLabel={option => `${option.companyIssuedId}-${option.name}`} filterOptions={(options, {
                inputValue
              }) => {
                const input = inputValue.toLowerCase();
                const matchOptions = options.filter(o => {
                  const label = `${o.companyIssuedId}-${o.name}`;
                  const customFields = o.customFields.map(c => c.textValue).join(":");
                  const contacts = (o.contacts ?? []).map(contact => `${contact.email}:${contact.firstName}:${contact.lastName}:${contact.mobile}:${contact.telephone}:${contact.customFields.filter(custom => custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_SINGLE || custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_MULTI).map(c => c.textValue).join(":")}`);
                  const matchLabel = label.toLowerCase().includes(input);
                  const matchSearch = `${customFields}:${contacts}`.toLowerCase().includes(input);
                  return matchLabel || matchSearch;
                });
                const matches = matchOptions.filter(option => {
                  const label = `${option.companyIssuedId}-${option.name}`;
                  return label.toLowerCase().includes(input.toLowerCase());
                });
                const nonMatches = matchOptions.filter(option => {
                  const label = `${option.companyIssuedId}-${option.name}`;
                  return !label.toLowerCase().includes(input.toLowerCase());
                });
                return [...matches, ...nonMatches];
              }} onChange={async (_, value, reason) => {
                if (reason !== "clear") {
                  await updateFile.mutateAsync({
                    id: file!._id,
                    input: {
                      client: value?._id
                    }
                  });
                }
                updateUpdateFileInput({
                  client: value?._id
                });
              }} onInput={e => {
                const event = e.nativeEvent as InputEvent;
                const input = e.target as any;
                if (event.inputType === "insertFromPaste") input.value = input.value.trim();
              }} onBlur={_e => {
                if (file?._client) {
                  updateUpdateFileInput({
                    client: file._client?._id
                  });
                }
              }} renderInput={params => <TextField {...params} label="Clients" />} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Typography color="text.secondary" variant="caption">
										Agent
									</Typography>
								</Grid>
								<Grid xs={12} sm={8}>
									<Autocomplete fullWidth options={agents} renderOption={(props, option, {
                inputValue
              }) => {
                const label = `${option.companyIssuedId}-${option.name}`;
                const matches = label.toLowerCase().indexOf(inputValue.toLowerCase());
                const parts = matches !== -1 ? [label.substring(0, matches), label.substring(matches, matches + inputValue.length), label.substring(matches + inputValue.length)] : [label];
                return <li {...props} key={option._id} style={{
                  whiteSpace: "pre-wrap"
                }}>
													{parts.map((part, index) => <Box key={index} component="span" sx={part.toLowerCase() === inputValue.toLowerCase() ? {
                    fontWeight: 800,
                    bgcolor: "primary.main"
                  } : {}}>
															{part}
														</Box>)}
												</li>;
              }} value={agents.find(agent => agent._id === till.updateFileInput?.agent) ?? null} isOptionEqualToValue={(option, value) => option._id === value?._id} getOptionLabel={option => `${option.companyIssuedId}-${option.name}`} filterOptions={(options, {
                inputValue
              }) => {
                const input = inputValue.toLowerCase();
                const matchOptions = options.filter(o => {
                  const label = `${o.companyIssuedId}-${o.name}`;
                  const customFields = o.customFields.map(c => c.textValue).join(":");
                  const contacts = (o.contacts ?? []).map(contact => `${contact.email}:${contact.firstName}:${contact.lastName}:${contact.mobile}:${contact.telephone}:${contact.customFields.filter(custom => custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_SINGLE || custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_MULTI).map(c => c.textValue).join(":")}`);
                  const matchLabel = label.toLowerCase().includes(input);
                  const matchSearch = `${customFields}:${contacts}`.toLowerCase().includes(input);
                  return matchLabel || matchSearch;
                });
                const matches = matchOptions.filter(option => {
                  const label = `${option.companyIssuedId}-${option.name}`;
                  return label.toLowerCase().includes(input.toLowerCase());
                });
                const nonMatches = matchOptions.filter(option => {
                  const label = `${option.companyIssuedId}-${option.name}`;
                  return !label.toLowerCase().includes(input.toLowerCase());
                });
                return [...matches, ...nonMatches];
              }} onChange={async (_, value, reason) => {
                if (reason !== "clear") {
                  await updateFile.mutateAsync({
                    id: file!._id,
                    input: {
                      agent: value?._id
                    }
                  });
                }
                updateUpdateFileInput({
                  agent: value?._id
                });
              }} onInput={e => {
                const event = e.nativeEvent as InputEvent;
                const input = e.target as any;
                if (event.inputType === "insertFromPaste") input.value = input.value.trim();
              }} onBlur={_e => {
                if (file?._agent) {
                  updateUpdateFileInput({
                    agent: file._agent?._id
                  });
                }
              }} renderInput={params => <TextField {...params} label="Agents" />} />
								</Grid>
								{customFields.map(field => [<Grid xs={12} sm={4} key={`${field._id}-label`}>
										<Typography color="text.secondary" variant="caption">
											{field.name}
										</Typography>
									</Grid>, <Grid xs={12} sm={8} key={`${field._id}-input`}>
										<CustomFieldRenderer field={field} value={customFieldValues[field._id]} onChange={async value => {
                const newCustomFields = customFieldValues;
                delete newCustomFields._customFields;
                const updatedCustomFieldValues = {
                  ...newCustomFields,
                  [value.customField]: value
                };
                await updateFile.mutateAsync({
                  id: file!._id,
                  input: {
                    customFields: Object.values(updatedCustomFieldValues)
                  }
                });
                setCustomFieldValues(updatedCustomFieldValues);
              }} />
									</Grid>])}
								{(me?.role === ROLE.SUPER_ADMIN || company?.isCustomFieldEnabled) && <Grid xs={12} textAlign="right">
										<Button startIcon={<BarChartSquarePlus />} onClick={openCustomFieldsDialog}>
											Manage custom fields
										</Button>
									</Grid>}
							</Grid>
							<Grid container spacing={3} xs={12} md={6} flexDirection="column">
								<Grid xs={12}>
									{productsTableItems.length && <ProductsTableSummary items={productsTableItems} itemsDisplay={fileItemsDisplay} subtotal={updateFileInputSummary.subtotal} additionalFee={updateFileInputSummary.additionalFeeAmount} fixDiscount={updateFileInputSummary.fixDiscountAmount} grandTotal={updateFileInputSummary.grossAmount} isUpdateOnDiscountChange={true} isUpdateOnAdditionalFeeChange={true} />}
								</Grid>
								<Grid xs={12}>
									<PaymentHistoryTable file={file!} company={company!} />
								</Grid>
								<Grid xs={12}>
									<Stack flexDirection="row-reverse" alignItems="center" gap={2}>
										{file?._receipt?.isFullyPaid ? <Chip sx={{
                  ".MuiChip-icon": {
                    color: "inherit"
                  }
                }} color="success" icon={<SvgIcon>
														<PiggyBank01 />
													</SvgIcon>} onClick={() => openPaymentDialog()} label="Paid" /> : <Button startIcon={<SvgIcon>
														<PiggyBank01 />
													</SvgIcon>} onClick={() => openPaymentDialog()}>
												Take payment
											</Button>}
										{isAdjustmentNeeded && <Button startIcon={<SvgIcon>
														<PiggyBank01 />
													</SvgIcon>} onClick={() => openPaymentDialog()}>
												Adjust payment
											</Button>}
									</Stack>
								</Grid>
								{company?.isPaymentVerificationEnabled && <>
										<Grid xs={12}>
											<Divider>
												<Typography variant="body2">
													Payment Verification Attachments
												</Typography>
											</Divider>
										</Grid>

										<Table>
											<TableHead>
												<TableRow>
													<TableCell sx={{
                      maxWidth: 120,
                      minWidth: 120
                    }}>
														Uploaded
													</TableCell>
													<TableCell>Attachment</TableCell>
													<TableCell sx={{
                      maxWidth: 100,
                      minWidth: 100
                    }}>
														Status
													</TableCell>
													<TableCell sx={{
                      maxWidth: 100,
                      minWidth: 100
                    }}>
														Finance
													</TableCell>
													<TableCell sx={{
                      maxWidth: 150,
                      minWidth: 150
                    }}>
														Action
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{(file?._paymentVerifications ?? []).map((a, i) => <TableRow key={`payment-verification-row-${i}`}>
														<TableCell>
															{$dayjs(a.createdAt).format(DATESTRING_FORMAT)}
														</TableCell>
														<TableCell>
															{a.attachment && <Box sx={{
                        display: "flex",
                        gap: 2,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        p: 1
                      }}>
																	{IMAGE_FILE_EXTENSIONS.includes(a.attachment.extension.toLocaleLowerCase()) && <img src={a.attachment.url} alt={a.attachment.name} style={{
                          height: 40,
                          width: 40,
                          borderRadius: 4
                        }} onClick={() => handleOpenViewPaymentVerificationDialog(a.attachment)} />}
																	{"pdf".includes(a.attachment.extension.toLocaleLowerCase()) && <PdfIcon01 onClick={() => handleOpenViewPaymentVerificationDialog(a.attachment)} />}
																	<Button variant="contained" color="inherit" sx={{
                          mr: 1
                        }} component="a" href={a.attachment.url} target="_blank" rel="noopener" download>
																		<SvgIcon color="action">
																			<Download />
																		</SvgIcon>
																	</Button>
																</Box>}
														</TableCell>
														<TableCell>
															{PAYMENT_VERIFICATION_UPLOAD_STATUS_MAP[a.paymentUploadStatus]}
														</TableCell>
														<TableCell>
															{a.status === PAYMENT_VERIFICATION_STATUS.VERIFIED ? "Verified" : "Reviewing"}
														</TableCell>
														<TableCell>
															<Button startIcon={<SvgIcon>
																		<PiggyBank01 />
																	</SvgIcon>} onClick={() => {
                        setCurrentPaymentVerification(a as unknown as PaymentVerification);
                        openPaymentDialogWithPaymentVerification();
                      }}>
																{a.status === PAYMENT_VERIFICATION_STATUS.VERIFIED ? "Edit payment" : "Take payment"}
															</Button>
														</TableCell>
													</TableRow>)}
											</TableBody>
										</Table>
										<Grid container mt={2} justifyContent="flex-end">
											{company?.isPaymentVerificationEnabled && <PaymentVerificationButton company={company} file={file!} />}
										</Grid>
									</>}
							</Grid>
							{company?.isFileCommentEnabled && <Grid container spacing={3} xs={12} md={12} flexDirection="column">
									<Grid xs={12}>
										<CommentSection isAttachmentEnabled={company?.isFileCommentAttachmentEnabled} />
									</Grid>
								</Grid>}
						</Grid>
						<Stack justifyContent="flex-end" alignItems="center" direction="row" spacing={1}>
							<LoadingButton variant="contained" color="error" loading={isFileProcessing} onClick={() => openDeleteFileDialogOpen()}>
								Delete
							</LoadingButton>
						</Stack>
					</>}
			</Stack>
		</Dialog>;
};
const PaymentDialog: FC<{
  onClose: () => void;
}> = ({
  onClose
}) => {
  const {
    query
  } = useRouter();
  const [prevPayments, setPrevPayments] = useState<GetFileByIdResults["_receipt"]>(undefined);
  const {
    data
  } = useGetCurrentCompany();
  const {
    data: file
  } = useGetCurrentFile();
  const payForFile = usePayForFile();
  const updateReceiptPayment = useUpdateReceiptPayment();
  const {
    updateFileInputSummary
  } = useTillSummary();
  const permissions = usePermissionFilters();
  const form = useFormik<PayForFileMutationVariables>({
    validateOnMount: true,
    initialValues: {
      id: String(query.fileId),
      input: {
        payments: []
      }
    },
    validationSchema: Yup.object({
      id: Yup.string().required("Required"),
      input: Yup.object({
        payments: Yup.array().of(Yup.object({
          payment_type: Yup.string().required("Required"),
          amount: Yup.number().required("Required"),
          bank_received_date: Yup.string()
        }).required("Required")).required("Required")
      })
    }),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {}
  });
  useEffect(() => {
    if (file) return setPrevPayments(file._receipt);
  }, [file]);
  return <Dialog open fullWidth maxWidth="xl" data-sentry-element="Dialog" data-sentry-component="PaymentDialog" data-sentry-source-file="view-file-modal.tsx">
			<Stack sx={{
      height: "100dvh",
      overflow: "auto"
    }} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
				<AppBar color="inherit" sx={{
        position: "relative"
      }} data-sentry-element="AppBar" data-sentry-source-file="view-file-modal.tsx">
					<Toolbar data-sentry-element="Toolbar" data-sentry-source-file="view-file-modal.tsx">
						<Typography sx={{
            ml: 2,
            flex: 1
          }} variant="h6" component="div" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
							Taking payment
						</Typography>
						<IconButton sx={{
            ml: 1
          }} onClick={onClose} data-sentry-element="IconButton" data-sentry-source-file="view-file-modal.tsx">
							<SvgIcon data-sentry-element="SvgIcon" data-sentry-source-file="view-file-modal.tsx">
								<X data-sentry-element="X" data-sentry-source-file="view-file-modal.tsx" />
							</SvgIcon>
						</IconButton>
					</Toolbar>
				</AppBar>
				<Grid container spacing={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
					<Grid xs={12} md={6} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						{(data?.paymentTypes ?? []).map(pt => <Grid key={pt._id} xs={6} md={4}>
								<Card variant="outlined" sx={[{
              height: "150px",
              cursor: "pointer",
              ":hover": {
                boxShadow: t => t.shadows[8]
              }
            }, form.values.input.payments.some(p => p.payment_type === pt._id) && {
              boxShadow: t => t.shadows[8],
              borderColor: "primary.main",
              backgroundColor: "primary.main",
              color: t => t.palette.primary.contrastText,
              ".MuiCardHeader-subheader": {
                color: t => t.palette.primary.contrastText
              }
            }]} onClick={() => {
              if (!form.values.input.payments.some(p => p.payment_type === pt._id)) {
                void form.setFieldValue("input.payments", [...form.values.input.payments, {
                  payment_type: pt._id,
                  amount: updateFileInputSummary.grossAmount - (file?._receipt?.payments.reduce((acc, cur) => acc + cur.payments.reduce((a, c) => a + c.amount, 0), 0) ?? 0) - form.values.input.payments.reduce((acc, cur) => acc + cur.amount, 0),
                  bank_received_date: undefined
                }]);
              }
            }}>
									<CardHeader title={pt.name} />
									<CardContent />
									<CardActions />
								</Card>
							</Grid>)}
					</Grid>
					<Grid xs={12} md={6} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<Grid xs={12} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Stack spacing={3} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
								<Divider data-sentry-element="Divider" data-sentry-source-file="view-file-modal.tsx">
									<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
										{" "}
										Selected payment types
									</Typography>
								</Divider>
								{form.values.input.payments.map((p, i) => <Grid key={`${p.payment_type}-container-${i}`} spacing={1} container>
										<Grid xs={12}>
											<Stack key={`${p.payment_type}-selected-${i}`} direction="row" spacing={1}>
												<div>
													<IconButton sx={{
                        mt: 1
                      }} color="error" onClick={() => {
                        void form.setFieldValue(`input.payments`, form.values.input.payments.filter(payment => payment.payment_type !== p.payment_type));
                      }}>
														<Trash01 />
													</IconButton>
												</div>
												<TextField type="number" fullWidth sx={{
                      flex: 1
                    }} label={data?.paymentTypes.find(t => t._id === p.payment_type)?.name} {...form.getFieldProps(`input.payments[${i}].amount`)}
                    // @ts-ignore
                    error={!!form.errors.input?.payments?.[i]?.amount}
                    // @ts-ignore
                    helperText={form.errors.input?.payments?.[i]?.amount} />
											</Stack>
										</Grid>
										<Grid xs={12}>
											<DateTimePicker sx={{
                    width: "100%"
                  }} label="Bank Received" value={form.values.input.payments?.[i]?.bank_received_date ? new Date(form.values.input.payments?.[i].bank_received_date as unknown as string) : null} onChange={value => {
                    if (!value) return;
                    void form.setFieldValue(`input.payments[${i}].bank_received_date`, value.toISOString());
                  }} />
										</Grid>
									</Grid>)}
							</Stack>
						</Grid>
					</Grid>
					<Grid xs={12} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						{file?._receipt && <Grid xs={12}>
								<Stack spacing={3}>
									<Divider>
										<Typography variant="body2">
											{" "}
											Previous payment types
										</Typography>
									</Divider>
									{prevPayments?.payments.map((payment, idx) => <>
											{payment.payments.map((p, i) => <Grid key={`previous-payment-${idx}`} xs={12} container spacing={2}>
													<Grid md={2} xs={6} display="flex" alignItems="center">
														<Typography variant="body2" alignItems="center">
															{$dayjs(payment.paidAt).format(DATETIMESTRING_FORMAT)}
														</Typography>
													</Grid>
													<Grid md={3} xs={6}>
														<FormControl fullWidth sx={{
                      flex: 1
                    }} disabled={!permissions.EDIT_PAYMENT_TYPE}>
															<InputLabel id="demo-simple-select-label">
																Payment Type
															</InputLabel>
															<Select labelId="payment-type-select-label" id="payment-type-select" value={p.payment_type} label="Payment Type" onChange={e => {
                        const updatedPayment = prevPayments;
                        updatedPayment.payments[idx].payments[i].payment_type = e.target.value;
                        setPrevPayments(updatedPayment);
                        if (!file._receipt) return;
                        void updateReceiptPayment.mutateAsync({
                          id: file._receipt._id,
                          input: {
                            paymentIndex: idx,
                            subPaymentIndex: i,
                            payment: {
                              ...updatedPayment.payments[idx].payments[i],
                              payment_type: e.target.value
                            }
                          }
                        });
                      }}>
																{data?.paymentTypes.map((pt, pi) => <MenuItem key={`sel-${pi}-${pt._id}`} value={pt._id}>
																		{pt.name}
																	</MenuItem>)}
															</Select>
														</FormControl>
													</Grid>
													<Grid md={3} xs={6}>
														<TextField disabled={!permissions.EDIT_PAYMENT_AMOUNT} type="number" fullWidth sx={{
                      flex: 1
                    }} label={data?.paymentTypes.find(t => t._id === p.payment_type)?.name} defaultValue={p.amount || 0} onBlur={e => {
                      const updatedPayment = prevPayments;
                      updatedPayment.payments[idx].payments[i].amount = Number(e.target.value);
                      setPrevPayments(updatedPayment);
                      if (!file._receipt) return;
                      void updateReceiptPayment.mutateAsync({
                        id: file._receipt._id,
                        input: {
                          paymentIndex: idx,
                          subPaymentIndex: i,
                          payment: {
                            ...updatedPayment.payments[idx].payments[i],
                            amount: Number(e.target.value)
                          }
                        }
                      });
                    }} />
													</Grid>
													<Grid md={4} xs={6}>
														<DateTimePicker sx={{
                      width: "100%"
                    }} label="Bank Received" value={p.bank_received_date ? new Date(p.bank_received_date) : null} onChange={value => {
                      if (!value) return;
                      const updatedPayment = prevPayments;
                      updatedPayment.payments[idx].payments[i].bank_received_date = value.toISOString();
                      setPrevPayments(updatedPayment);
                      if (!file._receipt) return;
                      void updateReceiptPayment.mutateAsync({
                        id: file._receipt._id,
                        input: {
                          paymentIndex: idx,
                          subPaymentIndex: i,
                          payment: {
                            ...updatedPayment.payments[idx].payments[i],
                            bank_received_date: value.toISOString()
                          }
                        }
                      });
                    }} />
													</Grid>
												</Grid>)}
										</>)}
								</Stack>
							</Grid>}
					</Grid>
					<Grid xs={12} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<Grid xs={12} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Stack spacing={3} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
								<Divider data-sentry-element="Divider" data-sentry-source-file="view-file-modal.tsx">
									<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx"> Payment Received</Typography>
								</Divider>
							</Stack>
						</Grid>
						<Grid alignContent="center" textAlign="right" xs={6} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
								Total Payment Received Amount:{" "}
							</Typography>
						</Grid>
						<Grid xs={6} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
								<TextField type="text" hiddenLabel sx={{
                flex: 1
              }} disabled value={formatNumber(file?._receipt?.payments ? file._receipt.payments.reduce((acc, cur) => acc + cur.payments.reduce((a, c) => a + c.amount, 0), 0) : 0)} fullWidth data-sentry-element="TextField" data-sentry-source-file="view-file-modal.tsx" />
							</Typography>
						</Grid>
						<Grid alignContent="center" textAlign="right" xs={6} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
								Outstanding Balance Amount:{" "}
							</Typography>
						</Grid>
						<Grid xs={6} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
								<TextField type="text" hiddenLabel disabled value={formatNumber(bigMath.sub(updateFileInputSummary.grossAmount, file?._receipt?.payments ? file._receipt.payments.reduce((acc, cur) => acc + cur.payments.reduce((a, c) => a + c.amount, 0), 0) : 0))} fullWidth data-sentry-element="TextField" data-sentry-source-file="view-file-modal.tsx" />
							</Typography>
						</Grid>
					</Grid>
				</Grid>
				<LoadingButton variant="contained" startIcon={<SvgIcon>
							<PiggyBank01 />
						</SvgIcon>} disabled={!form.isValid} loading={payForFile.isLoading} sx={{
        position: "absolute",
        bottom: 16,
        right: 16
      }} onClick={async () => {
        if (form.values.input.payments.length > 0) {
          await payForFile.mutateAsync(form.values);
        }
        onClose();
      }} data-sentry-element="LoadingButton" data-sentry-source-file="view-file-modal.tsx">
					Save
				</LoadingButton>
			</Stack>
		</Dialog>;
};
const PaymentDialogWithPaymentVerification: FC<{
  paymentVerification?: PaymentVerification;
  onClose: () => void;
}> = ({
  paymentVerification,
  onClose
}) => {
  const {
    query
  } = useRouter();
  const {
    data
  } = useGetCurrentCompany();
  const {
    data: file
  } = useGetCurrentFile();
  const payForFile = usePayForFile();
  const updatePaymentVerification = useUpdatePaymentVerification();
  const updateReceiptPayment = useUpdateReceiptPayment();
  const [prevPayments, setPrevPayments] = useState<GetFileByIdResults["_receipt"]>(undefined);
  const [paymentUploadStatus, setPaymentUploadStatus] = useState<PAYMENT_VERIFICATION_UPLOAD_STATUS>(paymentVerification!.paymentUploadStatus);
  const permissions = usePermissionFilters();
  const form = useFormik<PayForFileMutationVariables>({
    validateOnMount: true,
    initialValues: {
      id: String(query.fileId),
      input: {
        paymentVerificationId: paymentVerification?._id,
        payments: []
      }
    },
    validationSchema: Yup.object({
      id: Yup.string().required("Required"),
      input: Yup.object({
        paymentVerificationId: Yup.string().optional(),
        payments: Yup.array().of(Yup.object({
          payment_type: Yup.string().required("Required"),
          amount: Yup.number().required("Required"),
          bank_received_date: Yup.string()
        }).required("Required")).required("Required")
      })
    }),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {}
  });
  useEffect(() => {
    if (file?._receipt) {
      setPrevPayments(file._receipt);
    }
  }, [file]);
  return <Dialog open fullWidth maxWidth="xl" data-sentry-element="Dialog" data-sentry-component="PaymentDialogWithPaymentVerification" data-sentry-source-file="view-file-modal.tsx">
			<Stack sx={{
      height: "100dvh",
      overflow: "auto"
    }} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
				<AppBar color="inherit" sx={{
        position: "relative"
      }} data-sentry-element="AppBar" data-sentry-source-file="view-file-modal.tsx">
					<Toolbar data-sentry-element="Toolbar" data-sentry-source-file="view-file-modal.tsx">
						<Typography sx={{
            ml: 2,
            flex: 1
          }} variant="h6" component="div" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
							Taking payment
						</Typography>
						<IconButton sx={{
            ml: 1
          }} onClick={onClose} data-sentry-element="IconButton" data-sentry-source-file="view-file-modal.tsx">
							<SvgIcon data-sentry-element="SvgIcon" data-sentry-source-file="view-file-modal.tsx">
								<X data-sentry-element="X" data-sentry-source-file="view-file-modal.tsx" />
							</SvgIcon>
						</IconButton>
					</Toolbar>
				</AppBar>
				<Grid container spacing={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
					<Grid xs={12} md={12} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						{(data?.paymentTypes ?? []).map(pt => <Grid key={pt._id} xs={6} md={4}>
								<Card variant="outlined" sx={[{
              height: "150px",
              cursor: "pointer",
              ":hover": {
                boxShadow: t => t.shadows[8]
              }
            }, form.values.input.payments.some(p => p.payment_type === pt._id) && {
              boxShadow: t => t.shadows[8],
              borderColor: "primary.main",
              backgroundColor: "primary.main",
              color: t => t.palette.primary.contrastText,
              ".MuiCardHeader-subheader": {
                color: t => t.palette.primary.contrastText
              }
            }]} onClick={() => {
              if (!form.values.input.payments.some(p => p.payment_type === pt._id)) {
                void form.setFieldValue("input.payments", [...form.values.input.payments, {
                  payment_type: pt._id,
                  amount: undefined,
                  bank_received_date: undefined
                }]);
              }
            }}>
									<CardHeader title={pt.name} />
									<CardContent />
									<CardActions />
								</Card>
							</Grid>)}
					</Grid>
					<Grid xs={12} md={12} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<Grid xs={12} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Stack spacing={3} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
								<Divider data-sentry-element="Divider" data-sentry-source-file="view-file-modal.tsx">
									<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
										{" "}
										Selected payment types
									</Typography>
								</Divider>
								{form.values.input.payments.map((p, i) => <Grid key={`${p.payment_type}-container-${i}`} spacing={1} container>
										<Grid xs={6}>
											<Stack key={`${p.payment_type}-selected-${i}`} direction="row" spacing={1}>
												<div>
													<IconButton sx={{
                        mt: 1
                      }} color="error" onClick={() => {
                        void form.setFieldValue(`input.payments`, form.values.input.payments.filter(payment => payment.payment_type !== p.payment_type));
                      }}>
														<Trash01 />
													</IconButton>
												</div>
												<TextField type="number" fullWidth sx={{
                      flex: 1
                    }} label={data?.paymentTypes.find(t => t._id === p.payment_type)?.name} {...form.getFieldProps(`input.payments[${i}].amount`)}
                    // @ts-ignore
                    error={!!form.errors.input?.payments?.[i]?.amount}
                    // @ts-ignore
                    helperText={form.errors.input?.payments?.[i]?.amount} />
											</Stack>
										</Grid>
										<Grid xs={6}>
											<DateTimePicker sx={{
                    width: "100%"
                  }} label="Bank Received" value={form.values.input.payments?.[i]?.bank_received_date ? new Date(form.values.input.payments?.[i].bank_received_date as unknown as string) : null} onChange={value => {
                    if (!value) return;
                    void form.setFieldValue(`input.payments[${i}].bank_received_date`, value.toISOString());
                  }} />
										</Grid>
									</Grid>)}
							</Stack>
						</Grid>
					</Grid>
					{file?._receipt && <Grid xs={12}>
							<Stack spacing={3}>
								<Divider>
									<Typography variant="body2">
										{" "}
										Previous payment types
									</Typography>
								</Divider>
								{prevPayments?.payments.map((payment, idx) => <>
										{payment.paymentVerificationId && payment.paymentVerificationId === paymentVerification?._id && <>
													{payment.payments.map((p, i) => <Grid key={`previous-payment-${idx}`} xs={12} container spacing={2}>
															<Grid md={2} xs={6} display="flex" alignItems="center">
																<Typography variant="body2" alignItems="center">
																	{$dayjs(payment.paidAt).format(DATETIMESTRING_FORMAT)}
																</Typography>
															</Grid>
															<Grid md={3} xs={6}>
																<FormControl sx={{
                      flex: 1
                    }} fullWidth disabled={!permissions.EDIT_PAYMENT_TYPE}>
																	<InputLabel id="demo-simple-select-label">
																		Payment Type
																	</InputLabel>
																	<Select labelId="payment-type-select-label" id="payment-type-select" value={p.payment_type} label="Payment Type" onChange={e => {
                        const updatedPayment = prevPayments;
                        updatedPayment.payments[idx].payments[i].payment_type = e.target.value;
                        setPrevPayments(updatedPayment);
                        if (!file._receipt) return;
                        void updateReceiptPayment.mutateAsync({
                          id: file._receipt._id,
                          input: {
                            paymentIndex: idx,
                            subPaymentIndex: i,
                            payment: {
                              ...updatedPayment.payments[idx].payments[i],
                              payment_type: e.target.value
                            }
                          }
                        });
                      }}>
																		{data?.paymentTypes.map((pt, pi) => <MenuItem key={`sel-${pi}-${pt._id}`} value={pt._id}>
																				{pt.name}
																			</MenuItem>)}
																	</Select>
																</FormControl>
															</Grid>
															<Grid md={3} xs={6}>
																<TextField disabled={!permissions.EDIT_PAYMENT_AMOUNT} type="number" sx={{
                      flex: 1
                    }} fullWidth label={data?.paymentTypes.find(t => t._id === p.payment_type)?.name} defaultValue={p.amount || 0} onBlur={e => {
                      const updatedPayment = prevPayments;
                      updatedPayment.payments[idx].payments[i].amount = Number(e.target.value);
                      setPrevPayments(updatedPayment);
                      if (!file._receipt) return;
                      void updateReceiptPayment.mutateAsync({
                        id: file._receipt._id,
                        input: {
                          paymentIndex: idx,
                          subPaymentIndex: i,
                          payment: {
                            ...updatedPayment.payments[idx].payments[i],
                            amount: Number(e.target.value)
                          }
                        }
                      });
                    }} />
															</Grid>
															<Grid md={4} xs={6}>
																<DateTimePicker sx={{
                      width: "100%"
                    }} label="Bank Received" value={p.bank_received_date ? new Date(p.bank_received_date) : null} onChange={value => {
                      if (!value) return;
                      const updatedPayment = prevPayments;
                      updatedPayment.payments[idx].payments[i].bank_received_date = value.toISOString();
                      setPrevPayments(updatedPayment);
                      if (!file._receipt) return;
                      void updateReceiptPayment.mutateAsync({
                        id: file._receipt._id,
                        input: {
                          paymentIndex: idx,
                          subPaymentIndex: i,
                          payment: {
                            ...updatedPayment.payments[idx].payments[i],
                            bank_received_date: value.toISOString()
                          }
                        }
                      });
                    }} />
															</Grid>
														</Grid>)}
												</>}
									</>)}
							</Stack>
						</Grid>}

					{paymentVerification && <Grid xs={12} pb={8}>
							<Stack spacing={3}>
								<Divider>
									<Typography variant="body2">
										{" "}
										Payment Verification Details
									</Typography>
								</Divider>
								<Grid key={`payment-verification-details`} xs={12} container spacing={2} px={2}>
									<Grid md={4} xs={6}>
										<DateTimePicker sx={{
                  width: "100%"
                }} label="Payment Attachment Received Date" value={paymentVerification.receivedDate ? new Date(paymentVerification.receivedDate) : null} onAccept={async value => {
                  if (!value) return;
                  const receivedDate = value.toISOString();
                  if (!receivedDate) return;
                  await updatePaymentVerification.mutateAsync({
                    input: {
                      fileId: file!._id,
                      paymentVerificationId: paymentVerification._id,
                      receivedDate: receivedDate
                    }
                  });
                }} />
									</Grid>
									<Grid md={4} xs={6}>
										<TextField type="number" label="Received Amount" defaultValue={paymentVerification.receivedAmount} onBlur={async e => {
                  if (isNaN(parseFloat(e.target.value))) {
                    return;
                  } else {
                    const receivedAmount = parseFloat(e.target.value);
                    if (!receivedAmount) return;
                    await updatePaymentVerification.mutateAsync({
                      input: {
                        fileId: file!._id,
                        paymentVerificationId: paymentVerification._id,
                        receivedAmount: receivedAmount
                      }
                    });
                  }
                }} fullWidth />
									</Grid>
									<Grid md={4} xs={6}>
										<Box sx={{
                  display: "flex",
                  flexDirection: "column"
                }}>
											<FormControl fullWidth>
												<InputLabel id="payment-status-label">
													Payment Status
												</InputLabel>
												<Select labelId="payment-status-label" id="payment-status-select" value={paymentUploadStatus} label="Payment Status" onChange={async e => {
                      const status = e.target.value;
                      if (!status) return;
                      await updatePaymentVerification.mutateAsync({
                        input: {
                          fileId: file!._id,
                          paymentVerificationId: paymentVerification._id,
                          paymentUploadStatus: status as PAYMENT_VERIFICATION_UPLOAD_STATUS
                        }
                      });
                      void setPaymentUploadStatus(status as PAYMENT_VERIFICATION_UPLOAD_STATUS);
                    }}>
													<MenuItem key={PAYMENT_VERIFICATION_UPLOAD_STATUS.DEPOSIT_PAYMENT} value={PAYMENT_VERIFICATION_UPLOAD_STATUS.DEPOSIT_PAYMENT}>
														Deposit Payment
													</MenuItem>
													<MenuItem key={PAYMENT_VERIFICATION_UPLOAD_STATUS.PARTIAL_PAYMENT} value={PAYMENT_VERIFICATION_UPLOAD_STATUS.PARTIAL_PAYMENT}>
														Partial Payment
													</MenuItem>
													<MenuItem key={PAYMENT_VERIFICATION_UPLOAD_STATUS.FINAL_PAYMENT} value={PAYMENT_VERIFICATION_UPLOAD_STATUS.FINAL_PAYMENT}>
														Final Payment
													</MenuItem>
												</Select>
											</FormControl>
										</Box>
									</Grid>
								</Grid>
							</Stack>
						</Grid>}
				</Grid>
				<LoadingButton variant="contained" startIcon={<SvgIcon>
							<PiggyBank01 />
						</SvgIcon>} disabled={!form.isValid} loading={payForFile.isLoading} sx={{
        position: "absolute",
        bottom: 16,
        right: 16
      }} onClick={async () => {
        if (form.values.input.payments.length > 0) {
          await payForFile.mutateAsync(form.values);
        }
        onClose();
      }} data-sentry-element="LoadingButton" data-sentry-source-file="view-file-modal.tsx">
					Save
				</LoadingButton>
			</Stack>
		</Dialog>;
};
const PaymentHistoryTable: FC<{
  file: GetFileByIdResults;
  company: GetCurrentCompanyResults;
}> = ({
  file,
  company
}) => {
  if (!file._receipt) return;
  const itemsSummary = calculateFile(file as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>);
  return <Table data-sentry-element="Table" data-sentry-component="PaymentHistoryTable" data-sentry-source-file="view-file-modal.tsx">
			<TableHead data-sentry-element="TableHead" data-sentry-source-file="view-file-modal.tsx">
				<TableRow data-sentry-element="TableRow" data-sentry-source-file="view-file-modal.tsx">
					<TableCell sx={{
          maxWidth: 120,
          minWidth: 120
        }} data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">Created</TableCell>
					<TableCell sx={{
          maxWidth: 120,
          minWidth: 120
        }} data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">
						Bank Received
					</TableCell>
					<TableCell data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">Payment Type</TableCell>
					<TableCell data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">Payment Amount</TableCell>
				</TableRow>
			</TableHead>
			<TableBody data-sentry-element="TableBody" data-sentry-source-file="view-file-modal.tsx">
				{file._receipt.payments.map((payment, idx) => <TableRow key={`receipt-payment-${idx}`}>
						<TableCell>
							{$dayjs(payment.paidAt).format(DATESTRING_FORMAT)}
						</TableCell>
						<TableCell>
							{payment.payments.map((p, i) => <div key={`bank-received-${i}`}>
									{p.bank_received_date ? $dayjs(p.bank_received_date).format(DATESTRING_FORMAT) : ""}
								</div>)}
						</TableCell>
						<TableCell>
							{payment.payments.map((p, i) => <div key={`payment-type-${i}`}>
									{company.paymentTypes.find(pt => pt._id.toString() === p.payment_type.toString())?.name ?? "[UNKNOWN TYPE]"}
								</div>)}
						</TableCell>
						<TableCell align="right">
							{payment.payments.map((p, i) => <div key={`payment-amt-${p.amount}-${i}`}>
									$ {formatNumber(p.amount)}
								</div>)}
						</TableCell>
					</TableRow>)}
				<TableRow key="total-payment-received-amount" data-sentry-element="TableRow" data-sentry-source-file="view-file-modal.tsx">
					<TableCell data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx"></TableCell>
					<TableCell data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx"></TableCell>
					<TableCell align="right" data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">Total Payment Received Amount</TableCell>
					<TableCell align="right" data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">
						${" "}
						{formatNumber(file._receipt.payments ? file._receipt.payments.reduce((acc, cur) => acc + cur.payments.reduce((a, c) => a + c.amount, 0), 0) : 0)}
					</TableCell>
				</TableRow>
				<TableRow key="total-outstanding-amount" data-sentry-element="TableRow" data-sentry-source-file="view-file-modal.tsx">
					<TableCell data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx"></TableCell>
					<TableCell data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx"></TableCell>
					<TableCell align="right" data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">Outstanding Balance Amount</TableCell>
					<TableCell align="right" data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">
						${" "}
						{formatNumber(bigMath.sub(itemsSummary.grossAmount, file._receipt.payments ? file._receipt.payments.reduce((acc, cur) => acc + cur.payments.reduce((a, c) => a + c.amount, 0), 0) : 0))}
					</TableCell>
				</TableRow>
			</TableBody>
		</Table>;
};
const ViewPaymentVerificationDialog: FC<{
  attachment: PaymentVerification["attachment"];
  onClose: () => void;
}> = ({
  attachment,
  onClose
}) => {
  const {
    url,
    name,
    extension
  } = attachment;
  return <Dialog open fullWidth maxWidth="md" data-sentry-element="Dialog" data-sentry-component="ViewPaymentVerificationDialog" data-sentry-source-file="view-file-modal.tsx">
			<Stack sx={{
      height: "80dvh",
      overflow: "auto"
    }} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
				<IconButton color="inherit" onClick={onClose} data-sentry-element="IconButton" data-sentry-source-file="view-file-modal.tsx">
					<SvgIcon data-sentry-element="SvgIcon" data-sentry-source-file="view-file-modal.tsx">
						<XIcon data-sentry-element="XIcon" data-sentry-source-file="view-file-modal.tsx" />
					</SvgIcon>
				</IconButton>
				{IMAGE_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && <img src={url} alt={name} style={{
        maxWidth: "100%",
        borderRadius: 4
      }} />}
				{"pdf".includes(extension.toLocaleLowerCase()) && <iframe src={url} style={{
        maxWidth: "100%",
        height: "90%",
        borderRadius: 4
      }} />}
			</Stack>
		</Dialog>;
};